///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Last line of defence: parses the XML string that is about to be downloaded and checks
//? it against the constraints of the official INS schema
//? (http://www.intrastat.ro/doc/Handbook_XML_2025_EN.pdf).
//?
//? Nothing reaches the user's disk without passing this. It is deliberately independent
//? from the code that builds the string - it reads the finished document, exactly like
//? the INTRASTAT offline application does when importing it.
//-----------------------------------------------------------------------------------------

import {
    isKnownCn8,
    isKnownDeliveryTerms,
    isKnownEuCountry,
    isKnownTransNature,
    isKnownTransportMode,
    isKnownWorldCountry,
    getRequiredSupplUnit,
    REPORTING_COUNTRY,
} from './IntrastatRulesService'
import { VERSION as CN_VERSION } from '../databases/CodVamal'

const NS = 'http://www.intrastat.ro/xml/InsSchema'

//-----------------------------------------------------------------------------------------
//* \brief
//? Element order inside a declaration item, as defined by InsDeclarationItemType and its
//? two extensions. Elements marked optional may be missing but never out of order.
//-----------------------------------------------------------------------------------------
const ITEM_SEQUENCE = {
    arrival: [
        { name: 'Cn8Code',                  optional: false },
        { name: 'InvoiceValue',             optional: false },
        { name: 'StatisticalValue',         optional: true  },
        { name: 'NetMass',                  optional: false },
        { name: 'NatureOfTransactionACode', optional: false },
        { name: 'NatureOfTransactionBCode', optional: true  },
        { name: 'DeliveryTermsCode',        optional: false },
        { name: 'ModeOfTransportCode',      optional: false },
        { name: 'CountryOfOrigin',          optional: false },
        { name: 'InsSupplUnitsInfo',        optional: true  },
        { name: 'CountryOfConsignment',     optional: true  },
    ],
    dispatch: [
        { name: 'Cn8Code',                  optional: false },
        { name: 'InvoiceValue',             optional: false },
        { name: 'StatisticalValue',         optional: true  },
        { name: 'NetMass',                  optional: false },
        { name: 'NatureOfTransactionACode', optional: false },
        { name: 'NatureOfTransactionBCode', optional: true  },
        { name: 'DeliveryTermsCode',        optional: false },
        { name: 'ModeOfTransportCode',      optional: false },
        { name: 'CountryOfOrigin',          optional: false },
        { name: 'InsSupplUnitsInfo',        optional: true  },
        { name: 'CountryOfDestination',     optional: false },
        { name: 'PartnerCountryCode',       optional: false },
        { name: 'PartnerVatNr',             optional: false },
    ],
}

const HEADER_SEQUENCE = ['VatNr', 'FirmName', 'RefPeriod', 'CreateDt', 'ApplicationRef', 'ContactPerson', 'DTPDetails']

const textOf = (parent, name) => {
    const node = parent.getElementsByTagNameNS(NS, name)[0]
    return node ? node.textContent.trim() : null
}

//-----------------------------------------------------------------------------------------
//* \brief
//? PositiveLongType: xsd:long restricted with minExclusive 0 -> a whole number > 0.
//-----------------------------------------------------------------------------------------
const checkPositiveLong = (value, label, row, findings, field = null) => {
    if (value === null) {
        findings.push({ row, label, field, message: `${label} lipsește din fișier` })
        return
    }
    if (!/^\d+$/.test(value)) {
        findings.push({ row, label, field, message: `${label} "${value}" nu este un număr întreg` })
        return
    }
    if (parseInt(value, 10) <= 0) {
        findings.push({ row, label, field, message: `${label} este ${value}; schema cere o valoare mai mare decât 0` })
    }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Verifies that the direct children of a node appear in the order the schema declares.
//-----------------------------------------------------------------------------------------
const checkSequence = (node, sequence, row, findings, label) => {
    const present = Array.from(node.children).map(child => child.localName)
    const allowed = sequence.map(s => s.name)

    let cursor = 0
    for (const name of present) {
        const position = allowed.indexOf(name, cursor)
        if (position === -1) {
            if (allowed.includes(name)) {
                findings.push({ row, label, message: `Elementul <${name}> apare într-o ordine greșită` })
            }
            else {
                findings.push({ row, label, message: `Elementul <${name}> nu face parte din schemă` })
            }
            return
        }
        cursor = position
    }
    sequence.forEach(({ name, optional }) => {
        if (!optional && !present.includes(name)) {
            findings.push({ row, label, message: `Elementul obligatoriu <${name}> lipsește` })
        }
    })
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Validates one declaration item against the schema and the nomenclatures.
//-----------------------------------------------------------------------------------------
const validateItemNode = (node, isArrival, findings, checkNomenclature = true) => {
    const row = node.getAttribute('OrderNr') || '?'
    checkSequence(node, ITEM_SEQUENCE[isArrival ? 'arrival' : 'dispatch'], row, findings, 'Linie')

    // Cn8Code - pattern [0-9]{8} + nomenclature
    const cn8 = textOf(node, 'Cn8Code')
    if (cn8 === null || cn8 === '') {
        findings.push({ row, label: 'Cod NC8', field: 'Cn8Code', message: 'Codul NC8 lipsește' })
    }
    else if (!/^[0-9]{8}$/.test(cn8)) {
        findings.push({ row, label: 'Cod NC8', field: 'Cn8Code', message: `Codul NC8 "${cn8}" nu respectă formatul [0-9]{8}` })
    }
    //? Only meaningful against the matching year: a declaration written for another
    //? nomenclature would otherwise report most of its codes as non existent
    else if (checkNomenclature && !isKnownCn8(cn8)) {
        findings.push({ row, label: 'Cod NC8', field: 'Cn8Code', message: `Codul NC8 "${cn8}" nu există în Nomenclatorul Combinat ${CN_VERSION}` })
    }

    checkPositiveLong(textOf(node, 'InvoiceValue'), 'Valoarea fiscală', row, findings, 'InvoiceValue')
    checkPositiveLong(textOf(node, 'NetMass'), 'Greutatea netă', row, findings, 'NetMass')

    const statValue = textOf(node, 'StatisticalValue')
    if (statValue !== null) checkPositiveLong(statValue, 'Valoarea statistică', row, findings, 'StatisticalValue')

    // Nature of transaction
    const natureA = textOf(node, 'NatureOfTransactionACode')
    const natureB = textOf(node, 'NatureOfTransactionBCode')
    if (natureA === null || natureA === '') {
        findings.push({ row, label: 'Natura tranzacției', field: 'NatureOfTransactionBCode', message: 'Codul A al naturii tranzacției lipsește' })
    }
    else if (!/^\d$/.test(natureA)) {
        findings.push({ row, label: 'Natura tranzacției', field: 'NatureOfTransactionBCode', message: `Codul A "${natureA}" trebuie să fie o singură cifră` })
    }
    if (natureB !== null && natureB !== '' && !isKnownTransNature(natureB)) {
        findings.push({ row, label: 'Natura tranzacției', field: 'NatureOfTransactionBCode', message: `Codul B "${natureB}" nu există în nomenclator` })
    }
    if (natureA && natureB && !natureB.startsWith(`${natureA}.`)) {
        findings.push({ row, label: 'Natura tranzacției', field: 'NatureOfTransactionBCode', message: `Codul A "${natureA}" nu corespunde codului B "${natureB}"` })
    }

    // Delivery terms / mode of transport
    const terms = textOf(node, 'DeliveryTermsCode')
    if (!terms || !isKnownDeliveryTerms(terms)) {
        findings.push({ row, label: 'Condiție de livrare', field: 'DeliveryTermsCode', message: `Condiția de livrare "${terms || ''}" nu există în nomenclator` })
    }
    const transport = textOf(node, 'ModeOfTransportCode')
    if (!transport || !isKnownTransportMode(transport)) {
        findings.push({ row, label: 'Mod de transport', field: 'ModeOfTransportCode', message: `Modul de transport "${transport || ''}" nu există în nomenclator` })
    }

    // Countries
    const origin = textOf(node, 'CountryOfOrigin')
    if (!origin || !isKnownWorldCountry(origin)) {
        findings.push({ row, label: 'Țara de origine', field: 'CountryOfOrigin', message: `Țara de origine "${origin || ''}" nu există în nomenclator` })
    }

    const partnerCountryName = isArrival ? 'CountryOfConsignment' : 'CountryOfDestination'
    const partnerCountry = textOf(node, partnerCountryName)
    const partnerLabel = isArrival ? 'Țara de expediere' : 'Țara de destinație'
    if (!partnerCountry) {
        findings.push({ row, label: partnerLabel, field: partnerCountryName, message: `${partnerLabel} lipsește` })
    }
    else if (partnerCountry === REPORTING_COUNTRY) {
        findings.push({ row, label: partnerLabel, field: partnerCountryName, message: `${partnerLabel} nu poate fi "${REPORTING_COUNTRY}"` })
    }
    else if (!isKnownEuCountry(partnerCountry)) {
        findings.push({ row, label: partnerLabel, field: partnerCountryName, message: `${partnerLabel} "${partnerCountry}" nu este stat membru UE` })
    }

    // Supplementary units
    const supplNode = node.getElementsByTagNameNS(NS, 'InsSupplUnitsInfo')[0]
    //? the required unit comes from the same nomenclature as the codes, so it is only
    //? trustworthy when the declaration was written for the version we ship
    const requiredUnit = (cn8 && checkNomenclature) ? getRequiredSupplUnit(cn8) : null
    if (supplNode) {
        const unitCode = textOf(supplNode, 'SupplUnitCode')
        checkPositiveLong(textOf(supplNode, 'QtyInSupplUnits'), 'Cantitatea în UM suplimentară', row, findings, 'QtyInSupplUnits')
        if (requiredUnit === '') {
            findings.push({
                row, label: 'UM suplimentară',
                message: `Codul NC8 "${cn8}" nu are unitate de măsură suplimentară, dar fișierul conține una`
            })
        }
        else if (requiredUnit && unitCode !== requiredUnit) {
            findings.push({
                row, label: 'UM suplimentară',
                message: `Unitatea "${unitCode}" nu corespunde codului NC8 "${cn8}" (corect: "${requiredUnit}")`
            })
        }
    }
    else if (requiredUnit) {
        findings.push({
            row, label: 'UM suplimentară',
            message: `Codul NC8 "${cn8}" cere unitatea de măsură suplimentară "${requiredUnit}"`
        })
    }

    // Partner VAT (dispatches only)
    if (!isArrival) {
        const partnerVatCountry = textOf(node, 'PartnerCountryCode')
        const partnerVatNr = textOf(node, 'PartnerVatNr')
        if (!partnerVatCountry || !isKnownEuCountry(partnerVatCountry)) {
            findings.push({
                row, label: 'Cod TVA partener',
                message: `Prefixul de țară "${partnerVatCountry || ''}" al codului TVA nu este stat membru UE`
            })
        }
        if (!partnerVatNr) {
            findings.push({ row, label: 'Cod TVA partener', message: 'Codul TVA al partenerului lipsește' })
        }
    }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Validates the declaration header.
//-----------------------------------------------------------------------------------------
const validateHeaderNode = (node, findings) => {
    checkSequence(node, HEADER_SEQUENCE.map(name => ({
        name,
        optional: name === 'ApplicationRef' || name === 'DTPDetails',
    })), 'antet', findings, 'Antet')

    const vat = textOf(node, 'VatNr')
    if (!vat || !/^[0-9]{10}$/.test(vat)) {
        findings.push({ row: 'antet', label: 'CUI', message: `CUI-ul "${vat || ''}" nu respectă formatul [0-9]{10}` })
    }
    const period = textOf(node, 'RefPeriod')
    if (!period || !/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) {
        findings.push({ row: 'antet', label: 'Perioadă', message: `Perioada de referință "${period || ''}" nu are formatul AAAA-LL` })
    }
    const created = textOf(node, 'CreateDt')
    if (!created || isNaN(Date.parse(created))) {
        findings.push({ row: 'antet', label: 'Dată creare', message: `Data creării "${created || ''}" nu este o dată validă` })
    }
    if (!textOf(node, 'FirmName')) {
        findings.push({ row: 'antet', label: 'Firmă', message: 'Denumirea firmei lipsește' })
    }

    const dtp = node.getElementsByTagNameNS(NS, 'DTPDetails')[0]
    if (dtp) {
        const dtpVat = textOf(dtp, 'VatNr')
        if (!dtpVat || !/^[0-9]{10}$/.test(dtpVat)) {
            findings.push({ row: 'antet', label: 'CUI terț', message: `CUI-ul declarantului terț "${dtpVat || ''}" nu respectă formatul [0-9]{10}` })
        }
        const address = dtp.getElementsByTagNameNS(NS, 'DTPAddress')[0]
        if (!address) {
            findings.push({ row: 'antet', label: 'Adresă terț', message: 'Adresa declarantului terț lipsește' })
        }
        else {
            //? An empty <DTPAddress> is accepted by the official application and is what
            //? this generator emits. What it does NOT survive is a nomenclature backed
            //? code that is present but empty: it resolves to null and the import dies
            //? with a NullPointerException in ASUtils.validateDeclarationNomenclatures.
            //? So the rule is the opposite of the XSD - never demand these elements,
            //? but refuse to ship them empty.
            ['LocalityCode', 'CountyCode'].forEach(name => {
                const node = address.getElementsByTagNameNS(NS, name)[0]
                if (node && node.textContent.trim() === '') {
                    findings.push({
                        row: 'antet',
                        label: 'Adresă terț',
                        message: `<${name}> este prezent dar gol; aplicația oficială respinge fișierul. ` +
                                 `Elementul trebuie omis sau completat cu un cod valid din nomenclator`
                    })
                }
            })
        }
    }
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Validates a complete declaration document.
//? Returns { valid, findings } where findings is [{ row, label, message }].
//-----------------------------------------------------------------------------------------
export const validateIntrastatXml = (xmlString) => {
    const findings = []

    if (typeof DOMParser === 'undefined') {
        // No parser available (non browser environment) - do not block generation
        return { valid: true, findings }
    }

    let doc
    try {
        doc = new DOMParser().parseFromString(xmlString, 'application/xml')
    } catch (e) {
        return { valid: false, findings: [{ row: '-', label: 'Fișier', message: 'Fișierul XML nu a putut fi analizat' }] }
    }

    if (doc.getElementsByTagName('parsererror').length > 0) {
        return { valid: false, findings: [{ row: '-', label: 'Fișier', message: 'Fișierul XML generat nu este bine format' }] }
    }

    const root = doc.documentElement
    const ROOTS = ['InsNewArrival', 'InsRevisedArrival', 'InsNewDispatch', 'InsRevisedDispatch']
    if (!root || !ROOTS.includes(root.localName)) {
        return { valid: false, findings: [{ row: '-', label: 'Fișier', message: `Element rădăcină necunoscut "${root ? root.localName : ''}"` }] }
    }
    if (root.namespaceURI !== NS) {
        findings.push({ row: '-', label: 'Fișier', message: `Spațiul de nume "${root.namespaceURI || ''}" este incorect` })
    }
    if (root.getAttribute('SchemaVersion') !== '1.0') {
        findings.push({ row: '-', label: 'Fișier', message: 'Atributul SchemaVersion trebuie să fie "1.0"' })
    }

    const isArrival = root.localName.endsWith('Arrival')
    const isRevised = root.localName.startsWith('InsRevised')

    const versionsNode = root.getElementsByTagNameNS(NS, 'InsCodeVersions')[0]
    if (!versionsNode) {
        findings.push({ row: '-', label: 'Fișier', message: 'Secțiunea <InsCodeVersions> lipsește' })
    }

    //? A declaration must use the CN nomenclature of its own reference period. We only
    //? ship one year, so a file written for another one cannot have its codes checked -
    //? doing it anyway would report most of them as non existent and look like our bug.
    const declaredCnVer = versionsNode ? textOf(versionsNode, 'CnVer') : null
    const cnVersionMismatch = declaredCnVer !== null && declaredCnVer !== '' &&
        declaredCnVer !== String(CN_VERSION)

    const headerNode = root.getElementsByTagNameNS(NS, 'InsDeclarationHeader')[0]
    if (!headerNode) {
        findings.push({ row: '-', label: 'Fișier', message: 'Secțiunea <InsDeclarationHeader> lipsește' })
    }
    else {
        validateHeaderNode(headerNode, findings)
    }

    const itemName = isArrival ? 'InsArrivalItem' : 'InsDispatchItem'
    const itemNodes = Array.from(root.getElementsByTagNameNS(NS, itemName))
    // New declarations require at least one item, revised ones may be empty
    if (itemNodes.length === 0 && !isRevised) {
        findings.push({ row: '-', label: 'Fișier', message: `Declarația nu conține nicio linie <${itemName}>` })
    }
    itemNodes.forEach(node => validateItemNode(node, isArrival, findings, !cnVersionMismatch))

    return {
        valid: findings.length === 0,
        findings,
        itemCount: itemNodes.length,
        type: isArrival ? 'achizitii' : 'livrari',
        revised: isRevised,
        refPeriod: headerNode ? textOf(headerNode, 'RefPeriod') : null,
        cnVersion: declaredCnVer,
        appCnVersion: String(CN_VERSION),
        cnVersionMismatch,
    }
}
